import React from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../../../styles/theme';
import { BottomSheet } from '../primitives/BottomSheet';

interface Props {
  visible: boolean;
  recipeTitle: string;
  dayLabel: string;
  servings: number;
  /** Week default from the config — shown in the reset link. */
  weekDefault: number;
  onChange: (n: number) => void;
  onClose: () => void;
}

/**
 * Post-generation portions adjuster (design 'Configurer ma semaine', mobile):
 * big stepper + reset-to-week-default link. Writes straight onto the
 * committed meal — the effect is immediately visible on the card.
 */
export function PortionsSheet({ visible, recipeTitle, dayLabel, servings, weekDefault, onChange, onClose }: Props) {
  const { t } = useTranslation();
  return (
    <BottomSheet visible={visible} onClose={onClose} maxHeight="50%">
      <View style={styles.content}>
        <Text style={styles.heading}>Portions</Text>
        <Text style={styles.subtitle} numberOfLines={1}>{recipeTitle} · {dayLabel}</Text>

        <View style={styles.stepper}>
          <Pressable
            style={[styles.stepBtn, servings <= 1 && styles.stepBtnDisabled]}
            onPress={() => onChange(Math.max(1, servings - 1))}
          >
            <Feather name="minus" size={22} color={Paprika.textSecondary} />
          </Pressable>
          <View style={styles.valueWrap}>
            <Text style={styles.value}>{servings}</Text>
            <Text style={styles.valueUnit}>pers.</Text>
          </View>
          <Pressable
            style={[styles.stepBtn, servings >= 12 && styles.stepBtnDisabled]}
            onPress={() => onChange(Math.min(12, servings + 1))}
          >
            <Feather name="plus" size={22} color={Paprika.textSecondary} />
          </Pressable>
        </View>

        {servings !== weekDefault ? (
          <Pressable onPress={() => onChange(weekDefault)} style={styles.resetLink}>
            <Text style={styles.resetLinkText}>{t('thisWeek.resetToWeekDefault', { count: weekDefault })}</Text>
          </Pressable>
        ) : (
          <View style={styles.resetSpacer} />
        )}

        <Pressable style={styles.doneBtn} onPress={onClose}>
          <Feather name="check" size={16} color="#fff" />
          <Text style={styles.doneBtnText}>{t('common.done')}</Text>
        </Pressable>
      </View>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  content: {
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 8,
  },
  heading: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 20,
    color: Paprika.textPrimary,
  },
  subtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 12.5,
    color: Paprika.textMuted,
    marginTop: 3,
    marginBottom: 20,
  },
  stepper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 22,
    marginBottom: 16,
  },
  stepBtn: {
    width: 52,
    height: 52,
    borderRadius: 15,
    backgroundColor: Paprika.surfaceLowest,
    borderWidth: 1,
    borderColor: Paprika.border,
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 2px 8px rgba(82, 68, 60, 0.08)',
  } as any,
  stepBtnDisabled: {
    opacity: 0.4,
  },
  valueWrap: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 7,
    minWidth: 96,
    justifyContent: 'center',
  },
  value: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 44,
    color: Paprika.textPrimary,
  },
  valueUnit: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: Paprika.textMuted,
  },
  resetLink: {
    alignItems: 'center',
    marginBottom: 16,
  },
  resetLinkText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
    color: Paprika.primary,
  },
  resetSpacer: {
    height: 31,
  },
  doneBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: Paprika.primary,
    borderRadius: 100,
    paddingVertical: 15,
    marginBottom: 8,
  },
  doneBtnText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 15,
    color: '#fff',
  },
});
